import type { Route } from './+types/_app.$pageId';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { Region } from '@/components/region';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';

import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';

import maharTharOg from '/images/mahindra-thar-og.jpg';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { cn } from '@/lib/utils';

@PageType({
    name: 'Store Page',
    description: 'Custom store page layout with top, main, and bottom regions created from backoffice',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'top',
        name: 'Top Region',
        description: 'Top region of the page',
        maxComponents: 10,
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Main region for content',
        maxComponents: 20,
    },
    {
        id: 'bottom',
        name: 'Bottom Region',
        description: 'Bottom region of the page',
        maxComponents: 10,
    },
])
export class StorePageMetadata {
    @AttributeDefinition({
        id: 'hideheaderFooter',
        name: 'Hide Header & Footer',
        description: 'When true, only the logo will be visible in the header and the footer will be hidden.',
        type: 'boolean',
        defaultValue: false,
    })
    hideheaderFooter?: boolean;
}

export type StorePageData = {
    page: PageWithComponentData | null | ReturnType<typeof fetchPageWithComponentData>;
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    ogImageUrl: string;
    hideheaderFooter: boolean;
};

/**
 * Server-side loader function that fetches store page data.
 */
export async function loader(args: Route.LoaderArgs): Promise<StorePageData> {
    const logger = getLogger(args.context);
    const pageId = args.params.pageId;
    logger.debug(`StorePage: loader starting for pageId: ${pageId}`);

    const requestUrl = new URL(args.request.url);

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    const pagePromise = fetchPageWithComponentData(args, {
        pageId: pageId as string,
    });

    let page = null;
    try {
        page = await pagePromise;
    } catch {
        // Handled gracefully if page resolution fails
    }

    const pageData = page?.data as Record<string, unknown> | undefined;
    const pageCustom = (page as Record<string, unknown> | null | undefined)?.custom as
        | Record<string, unknown>
        | undefined;
    const hideheaderFooter = Boolean(
        pageData?.hideheaderFooter ??
            pageData?.hideHeaderFooter ??
            pageCustom?.hideheaderFooter ??
            pageCustom?.hideHeaderFooter ??
            (page as Record<string, unknown> | null | undefined)?.hideheaderFooter ??
            (page as Record<string, unknown> | null | undefined)?.hideHeaderFooter ??
            false
    );

    return {
        page: page ?? pagePromise,
        wishlistInitialState: fetchWishlistInitialState(args.context),
        pageUrl,
        ogImageUrl: new URL(maharTharOg, requestUrl.origin).href,
        hideheaderFooter,
    };
}

export default function StorePage({ loaderData }: { loaderData: StorePageData }) {
    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <div className={cn('pb-10 -mt-8 store-page', loaderData.hideheaderFooter && 'comingsoon-page')}>
                <SeoMeta
                    title={loaderData.page ? (loaderData.page.pageTitle ?? loaderData.page.name ?? '') : ''}
                    description={loaderData.page?.pageDescription ?? ''}
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                
                <Region
                    page={loaderData.page}
                    regionId="top"
                />

                <Region
                    page={loaderData.page}
                    regionId="main"
                />

                <Region
                    page={loaderData.page}
                    regionId="bottom"
                />
            </div>
        </WishlistProvider>
    );
}
